import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { kindOf } from "../../player";
import { upNextOf, type CastQueueItem } from "../../lib/castQueue";
import { useCastQueueStore } from "../../store/castQueueStore";
import { usePlayerControlsHold } from "./controls/usePlayerControlsVisibility";

/**
 * A link cast's queue in the dock (spec 2026-10-16 §4): "3 / 7 · Next: <title>" and a collapsible,
 * read-only up-next list. Hidden for a one-item queue (a pasted link) and while no queue is known;
 * a late joiner asks GET queue once.
 *
 * `fullScreen`: under the shared controls' top bar (Figma 4351:2409): "Next: <title>" only (the meta line carries
 * "2 of 5"), white on the scrim; the open up-next list holds the controls up.
 */
export default function CastQueueLine({ slotId, fullScreen = false }: { slotId: number; fullScreen?: boolean }) {
  const { t } = useTranslation();
  const queue = useCastQueueStore((s) => s.bySlot[slotId]?.queue ?? null);
  const [open, setOpen] = useState(false);
  const upNextShown = open && queue != null && queue.count > 1 && upNextOf(queue).length > 0;
  usePlayerControlsHold("upNext", fullScreen && upNextShown);

  useEffect(() => {
    useCastQueueStore.getState().ensure(slotId);
  }, [slotId]);

  if (!queue || queue.count <= 1) return null;
  const upNext = upNextOf(queue);
  const titleOf = (item: CastQueueItem) => item.title ?? kindOf(item).label(t);
  const position = t("cast_queue_position", "{{n}} / {{total}}", { n: queue.index + 1, total: queue.count });
  const next = upNext.length > 0 ? t("cast_queue_next", "Next: {{title}}", { title: titleOf(upNext[0]) }) : null;
  const line = fullScreen ? next : next ? `${position} · ${next}` : position;
  const listId = `cast-queue-list-${slotId}`;
  if (line == null) return null;

  return (
    <div data-testid="cast-queue" className={fullScreen ? "pb-1 text-xs leading-[1.3] text-white/85" : "px-2 py-1 text-[11px] text-slate-300"}>
      <div className="flex items-center gap-2">
        <span data-testid="cast-queue-line" className="min-w-0 flex-1 truncate">{line}</span>
        {upNext.length > 0 && (
          <button
            type="button"
            data-testid="cast-queue-toggle"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => setOpen((o) => !o)}
            className={fullScreen ? "shrink-0 rounded-full bg-white/16 px-2.5 py-1 text-white" : "shrink-0 underline"}
          >
            {open ? t("cast_queue_hide", "Hide up next") : t("cast_queue_show", "Up next")}
          </button>
        )}
      </div>
      {open && upNext.length > 0 && (
        <ol id={listId} data-testid="cast-queue-list" start={queue.index + 2} className="mt-1 flex max-h-32 list-decimal flex-col gap-0.5 overflow-y-auto pl-5">
          {upNext.map((item, i) => (
            <li key={queue.index + 1 + i} className="truncate">{titleOf(item)}</li>
          ))}
        </ol>
      )}
    </div>
  );
}
